import { Store } from 'vuex';
import gql from 'graphql-tag';
import { ApolloClient } from 'apollo-client';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import { Subscription } from 'apollo-client/util/Observable';
import lightStateFields from '@/queries/fields/lightStateFields';
import { roomFieldsJustMetadata } from '@/queries/fields/roomFields';
import _ from 'lodash';
import accessoryFields from '@/queries/fields/accessoryFields';
import sensorSettingsFields from '@/queries/fields/sensorSettingsFields';
import { PilotingEventHandler } from '@/helpers/subscription/PilotingEventHandler';

export default class SubscriptionHandler {
  stopWatchCallback: () => void | undefined;
  querySubscription: Subscription | undefined;

  constructor(
    store: Store<any>,
    client: ApolloClient<NormalizedCacheObject>,
  ) {
    // watch for the store changes, if current floor is changed -> we need
    // to resubscribe
    this.stopWatchCallback = this.watchStoreChange(store, client);
    // and start initial subscription with current floor id (if present)
    const floorId = store.state.user.currentFloorId;
    if (floorId) {
      this.querySubscription = this.subscribeToChanges(
        floorId,
        client,
        store,
      );
    }
  }

  watchStoreChange(
    store: Store<any>,
    client: ApolloClient<NormalizedCacheObject>,
  ): () => void {
    // don't forget to unsubscribe before subscribing again
    this.stopWatchCallback && this.stopWatchCallback();
    return store.watch(
      () => store.getters.currentFloorId,
      (value: string | undefined) => {
        if (value == undefined) {
          this.querySubscription && this.querySubscription.unsubscribe();
          return;
        }
        this.subscribeToChanges(value, client, store);
      },
    );
  }

  subscribeToChanges(
    floorId: string,
    client: ApolloClient<NormalizedCacheObject>,
    store: Store<any>,
  ): Subscription {
    // don't forget to unsubscribe before subscribing again
    this.querySubscription && this.querySubscription.unsubscribe();
    const subQuery = gql`
      subscription($id: String!) {
        floorUpdate(floorId: $id) {
          event
          data {
            ... on Light {
              id
              name
              currentState {
                ...lightStateFields
              }
            }
            ... on Room {
              ...roomFieldsJustMetadata
            }
            ... on Group {
              id
              name
            }
            ... on Accessory {
              ...accessoryFields
            }
          }
        }
      }
      ${roomFieldsJustMetadata}
      ${lightStateFields}
      ${accessoryFields}
      ${sensorSettingsFields}
    `;
    // subscribe to changes in the query
    const observer = client.subscribe({
      query: subQuery,
      variables: {
        id: floorId,
      },
      fetchPolicy: 'no-cache',
    });
    const pilotingEventsHandler = new PilotingEventHandler(floorId, client);
    // update cache when receiving new event
    const subscription = observer.subscribe({
      next(payload: any) {
        const elData = payload.data?.floorUpdate?.data;
        if (elData == undefined) {
          return;
        }
        if (payload.data.floorUpdate.event == 'discover/accessory') {
          store.dispatch('discoveredAccessory', { accessory: elData });
          return;
        } else if (payload.data.floorUpdate.event == 'update/light') {
          pilotingEventsHandler.updateCurrentStateInCache(elData.id, elData.currentState);
          return;
        }
      },
    });
    return subscription;
  }
}
